import { AxiosError } from 'axios';
import { authClient } from '../config/api';
import {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  PasswordRecoveryApiResponse,
  User,
  AuthTokens,
} from './types/auth.types';
import { tokenManager } from '../utils/tokenManager';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';
import { docmanService } from './docman.service';

export class EmailNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EmailNotFoundError';
  }
}

export class AuthService {
  async login(email: string, password: string): Promise<{ user: User; tokens: AuthTokens }> {
    try {
      const request: LoginRequest = { email, password };

      const response = await authClient.post<LoginResponse>('/login', request);

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Erro no login');
      }

      const { accessToken, refreshToken, expiresIn, user } = response.data.data;

      const tokens: AuthTokens = {
        accessToken,
        refreshToken,
        expiresIn,
      };

      tokenManager.setTokens(tokens);
      tokenManager.setUser(user);
      return { user, tokens };
    } catch (error: unknown) {
      tokenManager.clearTokens();
      apiErrorHandler(error, 'Erro ao realizar login');
    }
  }

  async refreshToken(): Promise<{ user: User; tokens: AuthTokens }> {
    try {
      const refreshToken = tokenManager.getRefreshToken();

      if (!refreshToken) {
        throw new Error('Refresh token não encontrado');
      }

      const request: RefreshTokenRequest = { refreshToken };

      const response = await authClient.post<RefreshTokenResponse>('/refresh', request);

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(response.data?.errorMessage || 'Erro ao renovar token');
      }

      const { accessToken, refreshToken: newRefreshToken, expiresIn, user } = response.data.data;

      const tokens: AuthTokens = {
        accessToken,
        refreshToken: newRefreshToken,
        expiresIn,
      };

      tokenManager.setTokens(tokens);
      tokenManager.setUser(user);

      return { user, tokens };
    } catch (error: unknown) {
      tokenManager.clearTokens();
      apiErrorHandler(error, 'Erro ao renovar token');
    }
  }

  async logout(): Promise<void> {
    try {
      const refreshToken = tokenManager.getRefreshToken();

      if (refreshToken) {
        await authClient.post('/logout', { refreshToken });
      }
    } catch {
      // Logout error - continue with local cleanup
    } finally {
      tokenManager.clearTokens();
      docmanService.clearCache();
    }
  }

  async requestPasswordRecovery(email: string): Promise<PasswordRecoveryApiResponse> {
    try {
      const response = await authClient.post<PasswordRecoveryApiResponse>('/password-recovery', {
        email: email.trim().toLowerCase(),
      });

      const data = response.data;

      if (
        !data.success &&
        data.errorMessage &&
        data.errorMessage.includes('Email não encontrado')
      ) {
        throw new EmailNotFoundError(data.errorMessage);
      }

      if (!data.success) {
        if (response.status === 429) {
          throw new Error('Muitas tentativas. Tente novamente mais tarde');
        }
        throw new Error(data.errorMessage || 'Erro ao enviar email de recuperação');
      }

      return data;
    } catch (error: unknown) {
      if (error instanceof EmailNotFoundError) {
        throw error;
      }

      if (error instanceof AxiosError && error.response?.data) {
        const data = error.response.data;

        if (data.errorMessage && data.errorMessage.includes('Email não encontrado')) {
          throw new EmailNotFoundError(data.errorMessage);
        }

        if (error.response.status === 429) {
          throw new Error('Muitas tentativas. Tente novamente mais tarde');
        }

        if (data.errorMessage) {
          throw new Error(data.errorMessage);
        }

        throw new Error('Erro ao enviar email de recuperação');
      }

      throw new Error('Erro de conexão. Verifique sua internet');
    }
  }

  async resetPassword(
    token: string,
    password: string,
    confirmPassword: string
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await authClient.post<{ success: boolean; message?: string }>(
        '/password-reset',
        {
          token,
          password,
          confirmPassword,
        }
      );

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.message || 'Erro ao redefinir senha');
      }

      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const data = error.response?.data;

        if (data?.errors && data.errors.length > 0) {
          throw new Error(data.errors.join('\n'));
        }

        if (data?.errorMessage) {
          throw new Error(data.errorMessage);
        }

        if (data?.message) {
          throw new Error(data.message);
        }
      }

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Erro ao redefinir senha');
    }
  }

  isAuthenticated(): boolean {
    return tokenManager.hasValidTokens();
  }

  getCurrentUser(): User | null {
    return tokenManager.getUser();
  }

  getTokens(): AuthTokens | null {
    return tokenManager.getTokens();
  }
}

export const authService = new AuthService();
